import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, ArrowUpRight } from "lucide-react";
import { api } from "../lib/api";
import { money, fmtDate, customerName } from "../lib/format";
import { PageHeader } from "../components/PageHeader";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";

const Metric = ({ label, value, sub, accent, testId, delay }) => (
  <div data-testid={testId} style={{ animationDelay: `${delay}ms` }} className={`animate-rise rounded-lg border bg-white p-5 ${accent ? "border-red-600 border-l-4" : "border-zinc-200"}`}>
    <div className="eyebrow">{label}</div>
    <div className="mt-2 font-display text-4xl font-bold">{value}</div>
    {sub && <div className="mt-1 font-mono text-xs text-zinc-500">{sub}</div>}
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => { api.get("/dashboard").then((r) => setData(r.data)); }, []);
  const st = (k) => data?.stats?.[k] || { count: 0, total: 0 };
  const decided = st("approved").count + st("declined").count;

  return (
    <div>
      <PageHeader eyebrow="Overview" title="Dashboard">
        <Button data-testid="dashboard-new-quote" onClick={() => navigate("/quotes/new")} className="bg-red-600 hover:bg-red-700"><Plus className="mr-1 h-4 w-4" />New quote</Button>
      </PageHeader>
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Metric testId="metric-approved" accent label="Approved value" value={money(st("approved").total)} sub={`${st("approved").count} quotes`} delay={0} />
        <Metric testId="metric-pending" label="Pending (draft + sent)" value={st("draft").count + st("sent").count} sub={money(st("draft").total + st("sent").total)} delay={60} />
        <Metric testId="metric-win-rate" label="Win rate" value={decided ? `${Math.round((st("approved").count / decided) * 100)}%` : "—"} sub={`${st("declined").count} declined`} delay={120} />
        <Metric testId="metric-customers" label="Customers" value={data?.customers ?? "—"} delay={180} />
      </div>
      <div className="mt-10 rounded-lg border border-zinc-200 bg-white">
        <div className="flex items-center justify-between border-b border-zinc-200 px-5 py-4">
          <h2 className="font-display text-lg font-bold uppercase">Recent quotes</h2>
          <Link to="/quotes" data-testid="view-all-quotes" className="flex items-center gap-1 text-sm font-medium text-red-600 hover:underline">View all<ArrowUpRight className="h-4 w-4" /></Link>
        </div>
        <table className="w-full text-sm">
          <tbody>
            {data?.recent?.map((q) => (
              <tr key={q.id} data-testid={`recent-quote-${q.number}`} onClick={() => navigate(`/quotes/${q.id}`)} className="cursor-pointer border-b border-zinc-100 transition-colors last:border-0 hover:bg-slate-50">
                <td className="px-5 py-3 font-mono font-semibold">{q.number}</td>
                <td className="px-3">{customerName(q.customer)}</td>
                <td className="px-3 text-zinc-500">{q.title}</td>
                <td className="px-3"><StatusBadge status={q.status} /></td>
                <td className="px-3 text-zinc-500">{fmtDate(q.created_at)}</td>
                <td className="px-5 text-right font-mono font-semibold">{money(q.total)}</td>
              </tr>
            ))}
            {data && !data.recent.length && <tr><td className="px-5 py-10 text-center text-zinc-400">No quotes yet — create your first one.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
